import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { countries } from '../../utils/countries';
import { cn } from '../../lib/utils';

interface CountryCodeSelectProps {
  value: string;
  onChange: (code: string) => void;
  className?: string;
  hasError?: boolean;
  id?: string;
  isDark?: boolean;
}

export function CountryCodeSelect({
  value,
  onChange,
  className = '',
  hasError = false,
  id,
  isDark = false,
}: CountryCodeSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});

  const selected =
    countries.find((c) => c.phoneCode === value) ||
    countries.find((c) => c.phoneCode === '+91') ||
    countries[0];

  const filtered = query.trim()
    ? countries.filter((c) => {
        const q = query.toLowerCase();
        return (
          c.name.toLowerCase().includes(q) ||
          c.phoneCode.includes(q) ||
          c.code.toLowerCase().includes(q)
        );
      })
    : countries;

  const seen = new Set<string>();
  const options = filtered.filter((c) => {
    if (seen.has(c.phoneCode)) return false;
    seen.add(c.phoneCode);
    return true;
  });

  const updatePanelPosition = useCallback(() => {
    const trigger = containerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const panelWidth = 288;
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const openUp = spaceBelow < 260 && rect.top > spaceBelow;
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - panelWidth - 8);

    setPanelStyle({
      position: 'fixed',
      left,
      width: panelWidth,
      zIndex: 3000,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + gap, top: 'auto' }
        : { top: rect.bottom + gap, bottom: 'auto' }),
    });
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePanelPosition();
    setTimeout(() => searchRef.current?.focus(), 50);
    setFocusedIndex(0);

    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [isOpen, updatePanelPosition]);

  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const item = listRef.current.children[focusedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex, isOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          setIsOpen(true);
        }
        return;
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((i) => Math.min(i + 1, options.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (options[focusedIndex]) {
          onChange(options[focusedIndex].phoneCode);
          setIsOpen(false);
          setQuery('');
        }
      }
    },
    [isOpen, options, focusedIndex, onChange]
  );

  const handleSelect = (code: string) => {
    onChange(code);
    setIsOpen(false);
    setQuery('');
  };

  const triggerClass = cn(
    'w-full h-full flex items-center gap-2 px-3 py-2.5 border-2 rounded-lg text-[13px] font-semibold focus:outline-none focus:ring-2 transition-all',
    isDark
      ? 'bg-[#0f172a] border-white/[0.08] text-slate-100 focus:border-[#0088cc]/45 focus:ring-[#0088cc]/25'
      : 'bg-[hsl(var(--card))] border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:border-transparent focus:ring-[hsl(var(--primary))]',
    hasError && 'border-red-500',
    isOpen &&
      !hasError &&
      (isDark
        ? 'border-[#0088cc]/45 ring-1 ring-[#0088cc]/25'
        : 'border-transparent ring-2 ring-[hsl(var(--primary))]')
  );

  const panelClass = cn(
    'border rounded-xl shadow-xl overflow-hidden',
    isDark ? 'bg-[#0f172a] border-white/[0.08]' : 'bg-[hsl(var(--card))] border-[hsl(var(--border))]'
  );

  const searchWrapClass = cn(
    'p-2 border-b',
    isDark ? 'border-white/[0.08] bg-[#111827]' : 'border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40'
  );

  const searchInputClass = cn(
    'w-full pl-8 pr-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2',
    isDark
      ? 'bg-[#0f172a] border-white/[0.08] text-slate-100 focus:border-[#0088cc]/45 focus:ring-[#0088cc]/25'
      : 'bg-[hsl(var(--card))] border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:border-transparent focus:ring-[hsl(var(--primary))]'
  );

  const dropdown = isOpen
    ? createPortal(
        <div ref={panelRef} className={panelClass} style={panelStyle} onKeyDown={handleKeyDown}>
          <div className={searchWrapClass}>
            <div className="relative">
              <svg
                className={cn(
                  'absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none',
                  isDark ? 'text-[#0088cc]/65' : 'text-[hsl(var(--muted-foreground))]'
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setFocusedIndex(0);
                }}
                placeholder="Search country or code…"
                className={searchInputClass}
              />
            </div>
          </div>

          <ul ref={listRef} role="listbox" className="max-h-56 overflow-y-auto py-1">
            {options.length === 0 ? (
              <li
                className={cn(
                  'px-4 py-3 text-sm text-center',
                  isDark ? 'text-[#0088cc]/65' : 'text-[hsl(var(--muted-foreground))]'
                )}
              >
                No results
              </li>
            ) : (
              options.map((c, i) => {
                const isSelected = c.phoneCode === value;
                const isFocused = i === focusedIndex;
                return (
                  <li
                    key={`${c.phoneCode}-${c.code}`}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(c.phoneCode)}
                    onMouseEnter={() => setFocusedIndex(i)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition-colors',
                      isFocused
                        ? isDark
                          ? 'bg-[#0088cc]/20'
                          : 'bg-[hsl(var(--primary))]/10'
                        : isDark
                          ? 'hover:bg-white/[0.04]'
                          : 'hover:bg-[hsl(var(--muted))]/60'
                    )}
                  >
                    <span className="text-base w-6 text-center shrink-0">{c.flag}</span>
                    <span
                      className={cn(
                        'font-semibold w-12 shrink-0',
                        isDark ? 'text-slate-100' : 'text-[hsl(var(--foreground))]'
                      )}
                    >
                      {c.phoneCode}
                    </span>
                    <span
                      className={cn(
                        'truncate flex-1',
                        isDark ? 'text-[#0088cc]/65' : 'text-[hsl(var(--muted-foreground))]'
                      )}
                    >
                      {c.name}
                    </span>
                    {isSelected && (
                      <svg
                        className="w-4 h-4 text-[hsl(var(--primary))] shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={containerRef} className={cn('relative', className)} onKeyDown={handleKeyDown}>
      <button
        id={id}
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className={triggerClass}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="text-base leading-none">{selected.flag}</span>
        <span className="font-medium whitespace-nowrap">{selected.phoneCode}</span>
        <svg
          className={cn(
            'w-3.5 h-3.5 ml-auto shrink-0 transition-transform',
            isDark ? 'text-[#0088cc]/65' : 'text-[hsl(var(--muted-foreground))]',
            isOpen && 'rotate-180'
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {dropdown}
    </div>
  );
}
